/* eslint-disable max-len */
import React, { useState, useContext, useCallback, useEffect, useMemo, useRef } from "react";
import { TextField, Checkbox, Card, Layout, PageActions } from "@shopify/polaris";
import { useMutation } from "react-apollo";
import validate from "app/appPermission/generic/yup";
import { PrivateContext } from "lib/context";
import { Banner, CopyButton } from "lib/components";
import { withFeature, withErrorBoundary } from "lib/hoc";
import constant from "lib/constant/constant";
import { errorHelper, storageHelper } from "lib/helpers";
import baseHelper from "lib/helpers/base";
import {
  applySavedChecklist,
  computeChecklistUpdate,
  extractChecklistState,
} from "lib/helpers/connectShopChecklist";

import SHOPIFY_INSTALL_SELLER_LOGIN from "app/appPermission/apollo/mutation/shopifySellerLogin";
import NEW_APP_DETAILS from "app/appPermission/apollo/mutation/newAppDetails";
import UPDATE_CONNECT_SHOP_CHECKLIST from "app/appPermission/apollo/mutation/updateConnectShopChecklist";

const OperatorAppPermission = (props) => {
  const { cms = {}, history, currentUser } = useContext(PrivateContext);
  const {
    gql,
    STEP,
    SETTINGS,
    SHOW,
    HIDE,
    API_ACCESS_TOKEN,
    REGISTERSUBDOMIN,
    API_APP_KEY,
    API_APP_SECRET_KEY,
    onboardingSteps,
    APP_URL_VALUE,
    SCOPE_VALUES,
    REDIRECT_URL_VALUE,
  } = constant;
  const adminScopesKey = "adminScopes";
  const installAppKey = "installApp";
  const { setSelectedTab } = props;

  const [errorMessage, setErrorMessage] = useState(false);
  const [shopifyInstallAndSellerLogin, { loading }] = useMutation(SHOPIFY_INSTALL_SELLER_LOGIN);
  const [newAppDetails, { loading: newApploading }] = useMutation(NEW_APP_DETAILS);
  const [saveConnectShopChecklist] = useMutation(UPDATE_CONNECT_SHOP_CHECKLIST);
  const saveChecklistTimeoutRef = useRef(null);
  const currentUri = window.location.hostname;

  const [banner, setBanner] = useState({
    action: null,
    isOpen: false,
    status: "",
    title: "",
    onDismiss: null,
  });

  const [values, setValues] = useState({
    goListingsPage: {
      checked: false,
      disable: false,
      order: 1,
      type: STEP,
      title: "On your Shopify store under settings, then go to the app listing page.",
    },
    developApps: {
      checked: false,
      disable: true,
      order: 2,
      type: STEP,
      title: cms("common.appPermission.label.developApps"),
    },
    customAppDev: {
      checked: false,
      disable: true,
      order: 3,
      type: STEP,
      title: "Build apps in Dev Dashboard",
    },
    createCustomAppDev: {
      checked: false,
      disable: true,
      order: 4,
      type: STEP,
      title: "Create app",
    },
    appMarketcube: {
      checked: false,
      disable: true,
      order: 5,
      type: STEP,
      title: "Enter app name, For eg: 'Marketplace Integration'",
    },
    appCreate: {
      checked: false,
      disable: true,
      order: 6,
      type: STEP,
      title: "Click 'Create'",
    },
    appUri: {
      checked: false,
      disable: true,
      order: 7,
      type: STEP,
      title: "In App URL, copy and paste the below URL",
    },
    appUriValue: {
      checked: false,
      disable: true,
      order: 8,
      type: APP_URL_VALUE,
      title: "App URL",
      value: `https://${currentUri}`,
    },
    adminScopes: {
      checked: false,
      disable: true,
      order: 9,
      type: STEP,
      title: "In Scopes, copy and paste the below scopes",
    },

    adminScopesValues: {
      checked: false,
      disable: true,
      order: 10,
      type: SCOPE_VALUES,
      title: "Scopes",
      value:
        "write_customers, read_customers, write_discounts, read_discounts, write_fulfillments, read_fulfillments, write_inventory, read_inventory, write_locations, read_locations, write_merchant_managed_fulfillment_orders, read_merchant_managed_fulfillment_orders, write_order_edits, read_order_edits, write_orders, read_orders, write_product_listings, read_product_listings, write_products, read_products, write_shipping, read_shipping",
    },
    redirectUri: {
      checked: false,
      disable: true,
      order: 11,
      type: STEP,
      title:
        `In Redirect URL, add the URL you added in App URL above with '/shopify/callback' at the end. For eg: 'https://${currentUri}/shopify/callback'`,
    },
    redirectUriValue: {
      checked: false,
      disable: true,
      order: 12,
      type: REDIRECT_URL_VALUE,
      title: "Redirect URL",
      value: `https://${currentUri}/shopify/callback`,
    },
    releaseVersion: {
      checked: false,
      disable: true,
      order: 13,
      type: STEP,
      title: "Scroll down and click on 'Release' button",
    },
    creds: {
      checked: false,
      disable: true,
      order: 14,
      type: STEP,
      step: 2,
      title:
        "On the sidebar click on 'Settings' and then under 'Credentials' copy 'Client ID' and 'Secret Key', paste them in the below given fields",
    },
    enableDist: {
      checked: false,
      disable: true,
      order: 15,
      type: STEP,
      step: 3,
      title: {
        intro: "On the sidebar:",
        bullets: [
          "Click 'App Name'",
          "Under 'Distribution', click 'Select distribution method'",
          "Don’t see the Distribution option? Install the app directly from the Install app button on the app home page.",
          "Select 'Custom distribution'",
          "Enter your store domain in the given field and paste the same domain in the below given field (e.g. 'abcstore.myshopify.com')",
          "Click 'Generate link'",
        ],
      },
    },
    pasteUri: {
      checked: false,
      disable: true,
      order: 16,
      type: STEP,
      step: 3,
      title:
        "Paste the given link into a new tab and hit enter, it will redirect you to authenticate your store, select your store and then it will take you to install the app",
    },
    installApp: { checked: false, disable: true, order: 17, title: cms("common.appPermission.label.installApp") },
  });

  const [apiDetails, setApiDetails] = useState({
    shopSubDomain: currentUser?.shop === "false" ? "" : currentUser?.shop + ".myshopify.com",
    apiKey: currentUser?.storeConfig?.shopify?.clientId || "",
    apiSecretKey: currentUser?.storeConfig?.shopify?.clientSecret || "",
  });

  const checklistConfig = useMemo(
    () => ({
      stepTypes: [STEP, SCOPE_VALUES, APP_URL_VALUE, REDIRECT_URL_VALUE],
      stepType: STEP,
      valueDisplayTypes: [SCOPE_VALUES, APP_URL_VALUE, REDIRECT_URL_VALUE],
      installAppKey,
      adminScopesKey,
      settingsType: SETTINGS,
    }),
    [STEP, SCOPE_VALUES, APP_URL_VALUE, REDIRECT_URL_VALUE, SETTINGS, adminScopesKey, installAppKey]
  );

  const persistChecklist = useCallback(
    (nextValues) => {
      if (saveChecklistTimeoutRef.current) {
        clearTimeout(saveChecklistTimeoutRef.current);
      }
      saveChecklistTimeoutRef.current = setTimeout(() => {
        saveConnectShopChecklist({
          variables: { input: { checklist: extractChecklistState(nextValues) } },
        });
      }, 400);
    },
    [saveConnectShopChecklist]
  );

  useEffect(() => {
    if (currentUser?.connectShopChecklist) {
      setValues((prev) => applySavedChecklist(prev, currentUser.connectShopChecklist, checklistConfig));
    }
  }, [checklistConfig, currentUser?.connectShopChecklist]);

  useEffect(
    () => () => {
      if (saveChecklistTimeoutRef.current) {
        clearTimeout(saveChecklistTimeoutRef.current);
      }
    },
    []
  );

  const onNext = () => {
    setSelectedTab(onboardingSteps.FINISH);
  };

  const toggleBanner = () =>
    setBanner({
      action: null,
      isOpen: false,
      status: "",
      title: "",
      onDismiss: null,
    });

  const handleChange = useCallback(
    (value) => {
      if (value === "creds") {
        const apiKey = apiDetails?.apiKey?.trim();
        const apiSecretKey = apiDetails?.apiSecretKey?.trim();
        if (!apiKey || !apiSecretKey) {
          handleValidation(API_APP_KEY, apiDetails?.apiKey);
          handleValidation(API_APP_SECRET_KEY, apiDetails?.apiSecretKey);
          return;
        }
      }
      if (value === "enableDist") {
        const shopSubDomain = apiDetails?.shopSubDomain?.trim();
        if (!shopSubDomain) {
          handleValidation(REGISTERSUBDOMIN, apiDetails?.shopSubDomain);
          return;
        }
      }

      const updateData = computeChecklistUpdate(values, value, checklistConfig);

      setValues((prevState) => {
        const nextValues = { ...prevState, ...updateData };
        if (Object.keys(updateData).length) {
          persistChecklist(nextValues);
        }
        return nextValues;
      });
    },
    [API_APP_KEY, API_APP_SECRET_KEY, REGISTERSUBDOMIN, apiDetails, checklistConfig, persistChecklist, values]
  );

  const handleField = (field, value) => {
    setApiDetails((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleValidation = async (field, value) => {
    const schema = {};

    const validationError = await validate(field, value, cms, schema);

    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
  };
  const isAnyError = () => Object.values(errorMessage).some((error) => error);

  const isAllValuesFilled = () =>
    Object.keys(values).every((key) => {
      if (checklistConfig.valueDisplayTypes?.includes(values[key].type)) {
        return true;
      }
      if (values[key].checked === false) {
        return false;
      }
      return typeof values[key].checked === "boolean" ? true : values[key];
    });

  const isAllAPIValuesFilled = () =>
    Object.keys(apiDetails).every((key) => {
      if (!apiDetails[key]) {
        return false;
      }
      return typeof apiDetails[key] === "boolean" ? true : apiDetails[key];
    });

  const onSubmit = async () => {
    try {
      const payload = {
        shopSubDomain: apiDetails?.shopSubDomain.trim(),
        apiKey: apiDetails?.apiKey.trim(),
        apiSecretKey: apiDetails?.apiSecretKey.trim(),
      };
      const res = await newAppDetails({
        variables: {
          input: payload,
        },
      });

      const responseData = baseHelper.getResponseData(res.data, "newAppDetails");

      if (responseData) {
        window.location.href = `https://${payload.shopSubDomain}/admin/oauth/authorize?client_id=${payload.apiKey
          }&scope=${values.adminScopesValues.value}&redirect_uri=${values.redirectUriValue.value
          }&state=${Math.random().toString(36).substring(2, 15)}`;

        return null;
        // onNext();
        // setTimeout(() => {
        //   history.push("/plan");
        // }, 2000);
      }

      let bannerValue = {
        status: constant.SUCCESS,
        title: cms("common.appPermission.message.success.select"),
        children: "",
        onDismiss: toggleBanner,
      };

      if (!responseData) {
        const errorResponse = baseHelper.getResponseError(res.data, "newAppDetails");
        console.log("🚀 ~ onSubmit ~ errorResponse:", errorResponse);
        bannerValue = { status: constant.CRITICAL, title: errorResponse };
        if (errorResponse === "Shop already connected.") {
          storageHelper.set("setupProgress", "finish");
          onNext();
        }
      }

      setBanner({
        ...bannerValue,
        isOpen: true,
      });

      /// end some error come
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const handleCancelButton = (event) => {
    event.preventDefault();
    const resetData = {};
    toggleBanner();
    Object.keys(values).forEach((item) => {
      resetData[item] = { ...values[item], checked: false, disable: true };
    });
    resetData.goListingsPage = { ...values.goListingsPage, checked: false, disable: false };
    setValues(resetData);
    setApiDetails({ shopSubDomain: "", apiAccessToken: "", apiKey: "", apiSecretKey: "" });
    setSelectedTab(onboardingSteps.COMMISSION);
  };

  const renderStepLabel = (title) => {
    if (title && typeof title === "object" && Array.isArray(title.bullets)) {
      return (
        <div>
          {title.intro ? <div>{title.intro}</div> : null}
          <ul style={{ paddingLeft: "15px", display: "flex", flexDirection: "column", gap: "5px", marginBottom: "0px" }}>
            {title.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
      );
    }
    return title;
  };

  return (
    <Layout id="app-permission">
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            isOpen={banner.isOpen}
            status={banner.status}
            title={banner.title}
            onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
          />
        </Layout.Section>
      )}
      <Layout.Section>
        <Card title="Configure Shopify App">
          <Card.Section sectioned>
            {values &&
              Object.keys(values)
                .filter((item) => item !== "creds" && item !== "enableDist" && item !== "pasteUri")
                .map((item) => {
                  if (values[item].type === STEP) {
                    return (
                      <div key={item}>
                        <Checkbox
                          value={item}
                          label={values?.[item]?.title}
                          disabled={values?.[item]?.disable}
                          checked={values?.[item]?.checked}
                          onChange={() => handleChange(item)}
                        />
                      </div>
                    );
                  }
                  if (values[item].type === SCOPE_VALUES) {
                    return (
                      <div key={item} style={{ margin: "10px 0px" }}>
                        <TextField
                          label={values?.[item]?.title}
                          disabled={false}
                          readOnly
                          connectedRight={
                            <CopyButton
                              values={values}
                              item={item}
                              handleChange={handleChange}
                              disableByFieldState={false}
                              trackProgressOnCopy={false}
                            />
                          }
                          value={values?.[item]?.value}
                        />
                      </div>
                    );
                  }
                  if (values[item].type === APP_URL_VALUE) {
                    return (
                      <div key={item} style={{ margin: "10px 0px" }}>
                        <TextField
                          label={values?.[item]?.title}
                          disabled={false}
                          readOnly
                          connectedRight={
                            <CopyButton
                              values={values}
                              item={item}
                              handleChange={handleChange}
                              disableByFieldState={false}
                              trackProgressOnCopy={false}
                            />
                          }
                          value={values?.[item]?.value}
                        />
                      </div>
                    );
                  }
                  if (values[item].type === REDIRECT_URL_VALUE) {
                    return (
                      <div key={item} style={{ margin: "10px 0px" }}>
                        <TextField
                          label={values?.[item]?.title}
                          disabled={false}
                          readOnly
                          connectedRight={
                            <CopyButton
                              values={values}
                              item={item}
                              handleChange={handleChange}
                              disableByFieldState={false}
                              trackProgressOnCopy={false}
                            />
                          }
                          value={values?.[item]?.value}
                        />
                      </div>
                    );
                  }

                  return null;
                })}
          </Card.Section>
        </Card>

        <Card title="Extract Credentials">
          <Card.Section sectioned>
            {values &&
              Object.keys(values).map((item) => {
                if (values?.[item]?.step === 2) {
                  return (
                    <div key={item}>
                      <Checkbox
                        value={item}
                        label={values?.[item]?.title}
                        disabled={values?.[item]?.disable}
                        checked={values?.[item]?.checked}
                        onChange={() => handleChange(item)}
                      />
                      <>
                        <div style={{ marginTop: "10px" }}>
                          <TextField
                            label="Client ID*"
                            id="apiKey"
                            disabled={values?.[item]?.disable}
                            type={constant.TEXT}
                            error={errorMessage && errorMessage.apiKey}
                            value={apiDetails?.apiKey}
                            onChange={(value) => handleField(API_APP_KEY, value)}
                            onBlur={() => handleValidation(API_APP_KEY, apiDetails?.apiKey)}
                          />
                        </div>
                        <br />
                        <div>
                          <TextField
                            label="Secret Key*"
                            id="apiSecretKey"
                            disabled={values?.[item]?.disable}
                            type={constant.TEXT}
                            error={errorMessage && errorMessage.apiSecretKey}
                            value={apiDetails?.apiSecretKey}
                            onChange={(value) => handleField(API_APP_SECRET_KEY, value)}
                            onBlur={() => handleValidation(API_APP_SECRET_KEY, apiDetails?.apiSecretKey)}
                          />
                        </div>
                        <br />
                      </>
                    </div>
                  );
                }

                return null;
              })}
          </Card.Section>
        </Card>

        <Card title="Select Distribution">
          <Card.Section sectioned>
            {values &&
              Object.keys(values).map((item) => {
                if (values?.[item]?.step === 3) {
                  return (
                    <div key={item}>
                      <Checkbox
                        value={item}
                        label={renderStepLabel(values?.[item]?.title)}
                        disabled={values?.[item]?.disable}
                        checked={values?.[item]?.checked}
                        onChange={() => handleChange(item)}
                      />
                      {values?.[item]?.step === 3 && item === "enableDist" ? (
                        <>
                          <div style={{ margin: "10px 0px" }}>
                            <TextField
                              label={`${cms("common.appPermission.shop.label")}*`}
                              error={errorMessage && errorMessage.shopSubDomain}
                              id="shopSubDomain"
                              disabled={values?.[item]?.disable}
                              type={constant.TEXT}
                              value={apiDetails?.shopSubDomain}
                              onChange={(value) => handleField(REGISTERSUBDOMIN, value)}
                              onBlur={() => handleValidation(REGISTERSUBDOMIN, apiDetails?.shopSubDomain)}
                              placeholder="abcstore.myshopify.com"
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  );
                }

                return null;
              })}
          </Card.Section>
        </Card>

        <Card title="Install App">
          <Card.Section sectioned>
            <Checkbox
              value="installApp"
              label={values?.installApp?.title}
              disabled={values?.installApp?.disable}
              checked={values?.installApp?.checked}
              onChange={() => handleChange(installAppKey)}
            />
          </Card.Section>
        </Card>
        <PageActions
          primaryAction={{
            id: "MC_PORTAL_ONBOARDING_STEP_05",
            content: cms("common.button.nextStep"),
            onAction: () => onSubmit(),
            disabled: !isAllValuesFilled() || !isAllAPIValuesFilled() || isAnyError(),
            loading: loading || newApploading,
          }}
          secondaryActions={[
            {
              content: cms("common.button.previous"),
              id: "previous",
              onAction: (e) => handleCancelButton(e),
            },
          ]}
        />
      </Layout.Section>
    </Layout>
  );
};

export default withFeature(withErrorBoundary(OperatorAppPermission), {});
